import type {
  CreateFetchRequestApiOptions,
  FetchBaseRequestApi,
  FetchBody,
  FetchGetMethod,
  FetchMethod,
  FetchRequestApi,
} from './types';

function wrapFetchMethod<TData>(
  call: FetchMethod,
  request: FetchBaseRequestApi<TData>,
  options: CreateFetchRequestApiOptions,
  invalidateCache: boolean
): FetchMethod {
  return async <TResponse = unknown>(
    routeOrBody?: string | FetchBody,
    body?: FetchBody
  ) => {
    const response = await call<TResponse>(routeOrBody, body);
    if (options.throwOnError && request.error) {
      throw request.error;
    }
    if (invalidateCache) {
      request.cache.clear();
    }
    if (options.transformResponse) {
      return options.transformResponse(response) as TResponse;
    }
    return response;
  };
}

function wrapFetchGetMethod(
  call: FetchGetMethod,
  request: FetchBaseRequestApi,
  options: CreateFetchRequestApiOptions
): FetchGetMethod {
  return async <TResponse = any>(route?: string) => {
    const response = await call<TResponse>(route);
    if (options.throwOnError && request.error) {
      throw request.error;
    }
    if (options.transformResponse) {
      return options.transformResponse(response) as TResponse;
    }
    return response;
  };
}

export function createFetchRequestApi<TData = unknown>(
  request: FetchBaseRequestApi<TData>,
  options: CreateFetchRequestApiOptions
): FetchRequestApi<TData> {
  const invalidateCacheOnMutation =
    options.invalidateCacheOnMutation !== false;
  const del = wrapFetchMethod(
    request.del,
    request,
    options,
    invalidateCacheOnMutation
  );
  const wrappedRequest = Object.defineProperties(
    {},
    Object.getOwnPropertyDescriptors(request)
  ) as FetchRequestApi<TData>;
  return Object.assign(wrappedRequest, {
    get: wrapFetchGetMethod(
      request.get,
      request,
      options
    ),
    getCacheKey: options.getCacheKey,
    post: wrapFetchMethod(
      request.post,
      request,
      options,
      invalidateCacheOnMutation
    ),
    put: wrapFetchMethod(
      request.put,
      request,
      options,
      invalidateCacheOnMutation
    ),
    patch: wrapFetchMethod(
      request.patch,
      request,
      options,
      invalidateCacheOnMutation
    ),
    del,
    delete: del,
  });
}
